import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { useOrganizationId } from './useOrganizationId';
import { useUserProfile } from '@/contexts/UserProfileContext';
import { fetchContactsMap } from '@/lib/supabaseHelpers';


type Concert = Tables<'concerts'>;
type ConcertInsert = TablesInsert<'concerts'>;
type ConcertUpdate = TablesUpdate<'concerts'>;

export function useConcerts() {
  const { session } = useUserProfile();
  
  return useQuery({
    queryKey: ['concerts'],
    queryFn: async () => {
      
      // Fetch concerts without contacts JOIN to avoid RLS issues
      const { data: concerts, error } = await supabase
        .from('concerts')
        .select(`
          *,
          venue:venues(id, name, city, region, country),
          artistic_project:artistic_projects(id, name, color),
          concert_series:concert_series(id, name)
        `)
        .order('date', { ascending: false });


      if (error) throw error;
      if (!concerts) return [];

      // Collect all unique contact IDs and resolve via centralized helper
      const contactsMap = await fetchContactsMap(
        concerts.flatMap(c => [c.performer_contact_id, c.organizer_contact_id]),
      );


      // Enrich concerts with contact names
      return concerts.map(concert => ({
        ...concert,
        performer: concert.performer_contact_id 
          ? contactsMap.get(concert.performer_contact_id) || { id: concert.performer_contact_id, name: 'Unknown' }
          : null,
        organizer: concert.organizer_contact_id
          ? contactsMap.get(concert.organizer_contact_id) || { id: concert.organizer_contact_id, name: 'Unknown' }
          : null,
      }));
    },
    enabled: !!session,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}

export function useConcert(id: string | undefined) {
  return useQuery({
    queryKey: ['concert', id],
    queryFn: async () => {
      if (!id) return null;
      
      // Fetch concert without contacts JOIN to avoid RLS issues
      const { data: concert, error } = await supabase
        .from('concerts')
        .select(`
          *,
          venue:venues(id, name, city, region, country),
          artistic_project:artistic_projects(id, name, color),
          concert_series:concert_series(id, name)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!concert) return null;

      // Fetch contact names separately
      const contactIds: string[] = [];
      if (concert.performer_contact_id) contactIds.push(concert.performer_contact_id);
      if (concert.organizer_contact_id) contactIds.push(concert.organizer_contact_id);

      let contactsMap = new Map<string, { id: string; name: string }>();
      if (contactIds.length > 0) {
        const { data: contacts, error: contactsError } = await supabase
          .from('contacts')
          .select('id, name')
          .in('id', contactIds);

        if (!contactsError && contacts) {
          contacts.forEach(contact => {
            contactsMap.set(contact.id, contact);
          });
        }
      }

      // Enrich concert with contact names
      return {
        ...concert,
        performer: concert.performer_contact_id
          ? contactsMap.get(concert.performer_contact_id) || { id: concert.performer_contact_id, name: 'Unknown' }
          : null,
        organizer: concert.organizer_contact_id
          ? contactsMap.get(concert.organizer_contact_id) || { id: concert.organizer_contact_id, name: 'Unknown' }
          : null,
      };
    },
    enabled: !!id,
  });
}

export function useVenuesForSelect() {
  const { session } = useUserProfile();
  
  return useQuery({
    queryKey: ['venues-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('venues')
        .select('id, name, city, region, country, ticket_commission_percentage, typical_rental_cost')
        .order('name');

      if (error) throw error;
      return data;
    },
    enabled: !!session,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
  });
}

export function useArtistContacts() {
  const { session } = useUserProfile();
  
  return useQuery({
    queryKey: ['artist-contacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, name')
        .eq('role', 'artist')
        .or('is_active.is.null,is_active.eq.true')
        .order('name');

      
      if (error) throw error;
      return data;
    },
    enabled: !!session,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
  });
}

export function useOrganizerContacts() {
  const { session } = useUserProfile();
  
  return useQuery({
    queryKey: ['organizer-contacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select('id, name, processing_fee_percentage')
        .in('role', ['promoter', 'booking_manager', 'organizer', 'venue_manager'])
        .or('is_active.is.null,is_active.eq.true')
        .order('name');

      
      if (error) throw error;
      return data;
    },
    enabled: !!session,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: true,
  });
}

export function useCreateConcert() {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationId();
  const { session } = useUserProfile();

  return useMutation({
    mutationFn: async (concert: Omit<ConcertInsert, 'created_by_user_id'>) => {
      if (!session?.user?.id) {
        throw new Error('User must be logged in to create concerts');
      }
      
      const { data, error } = await supabase
        .from('concerts')
        .insert({
          ...concert,
          organization_id: organizationId,
          created_by_user_id: session.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // IMMEDIATE: Only invalidate core concert queries
      queryClient.invalidateQueries({ queryKey: ['concerts'] });
      queryClient.invalidateQueries({ queryKey: ['concert'] });
      
      // Analytics/dashboard refreshed immediately
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['concert-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['all-revenue-shares'] });
      queryClient.invalidateQueries({ queryKey: ['artist-availability'] });
    },
  });
}

export function useUpdateConcert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...concert }: ConcertUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('concerts')
        .update(concert)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (error.message?.includes('row-level security') || error.code === '42501') {
          throw new Error('Permission denied: Only admins and the organizing booking manager can edit this concert.');
        }
        throw error;
      }
      return data;
    },
    // OPTIMISTIC UPDATE: Update cache BEFORE server response
    onMutate: async (updatedConcert) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['concerts'] });
      await queryClient.cancelQueries({ queryKey: ['concert', updatedConcert.id] });
      
      // Snapshot previous values for rollback
      const previousConcerts = queryClient.getQueryData(['concerts']);
      const previousConcert = queryClient.getQueryData(['concert', updatedConcert.id]);
      
      // Optimistically update the concerts list
      queryClient.setQueryData(['concerts'], (old: any[] | undefined) => 
        old?.map(c => c.id === updatedConcert.id 
          ? { ...c, ...updatedConcert } 
          : c
        )
      );
      
      // Optimistically update the single concert
      queryClient.setQueryData(['concert', updatedConcert.id], (old: any) => 
        old ? { ...old, ...updatedConcert } : old
      );
      
      return { previousConcerts, previousConcert, concertId: updatedConcert.id };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousConcerts) {
        queryClient.setQueryData(['concerts'], context.previousConcerts);
      }
      if (context?.previousConcert) {
        queryClient.setQueryData(['concert', context.concertId], context.previousConcert);
      }
    },
    onSettled: (data, error, variables) => {
      // IMMEDIATE: Only invalidate core concert queries
      queryClient.invalidateQueries({ queryKey: ['concerts'] });
      queryClient.invalidateQueries({ queryKey: ['concert', variables.id] });
      
      // Analytics/dashboard refreshed immediately
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['concert-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['all-revenue-shares'] });
      queryClient.invalidateQueries({ queryKey: ['artist-availability'] });
    },
  });
}

export function useDeleteConcert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('concerts')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      // IMMEDIATE: Only invalidate core concert queries
      queryClient.invalidateQueries({ queryKey: ['concerts'] });
      queryClient.invalidateQueries({ queryKey: ['concert'] });
      queryClient.invalidateQueries({ queryKey: ['revenue-shares'] });
      
      // Analytics/dashboard refreshed immediately
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['concert-analytics'] });
      queryClient.invalidateQueries({ queryKey: ['all-revenue-shares'] });
      queryClient.invalidateQueries({ queryKey: ['artist-availability'] });
    },
  });
}
